// pdfQuote — find a quoted passage on a rendered PDF page (#89): which of the
// page's text items hold the quote, and where they sit, as fractions of the
// page so a highlight scales with the canvas.
//
// Matching is loose like the rest of the app's quote matching (case and
// whitespace insensitive): a claim's quote is verbatim source text, but the
// page's text items split it at line ends and font runs. A quote that is not
// found whole is tried by its first words (the start of a passage is enough
// to place the marker). A scanned page has no text items, so nothing is found
// there: the caller then marks the page instead.
//
// Pure: values in, values out. The geometry follows pdf.js (`Util.transform`
// of the viewport transform with an item's text matrix).

export type PdfTextItem = {
    readonly str: string;
    // The item's text matrix and its advance width (pdf.js `TextItem`).
    readonly transform: readonly number[];
    readonly width: number;
    readonly height: number;
};

// A rectangle as fractions (0..1) of the page's width and height.
export type PageRect = {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
};

// Each character of the joined page text, lower-cased, with the item it
// came from (-1 for the space joining two items); whitespace runs collapse
// to one space.
const charsOf = (
    items: readonly string[],
): { readonly text: string; readonly owner: readonly number[] } => {
    const out: { c: string; i: number }[] = [];
    const push = (c: string, i: number) => {
        const space = /\s/.test(c);
        const last = out.at(-1);
        if (space && (last === undefined || last.c === " ")) return;
        out.push({ c: space ? " " : c, i });
    };
    items.forEach((s, i) => {
        for (const c of s.toLocaleLowerCase()) push(c, i);
        push(" ", -1);
    });
    return { text: out.map((x) => x.c).join(""), owner: out.map((x) => x.i) };
};

const normalize = (s: string): string =>
    s.toLocaleLowerCase().replace(/\s+/g, " ").trim();

// The indices of the items the quote covers ([] when it is not on the page).
export const quoteItems = (
    items: readonly string[],
    quote: string,
): readonly number[] => {
    const { text, owner } = charsOf(items);
    const whole = normalize(quote);
    const start = whole.split(" ").slice(0, 8).join(" ");
    const tries = [whole, start].filter((q) => q.length >= 12);
    for (const q of tries) {
        const at = text.indexOf(q);
        if (at < 0) continue;
        return [...new Set(owner.slice(at, at + q.length))].filter(
            (i) => i >= 0,
        );
    }
    return [];
};

// pdf.js `Util.transform`: the product of two affine matrices.
const multiply = (
    m1: readonly number[],
    m2: readonly number[],
): readonly number[] => {
    const [a1 = 0, b1 = 0, c1 = 0, d1 = 0, e1 = 0, f1 = 0] = m1;
    const [a2 = 0, b2 = 0, c2 = 0, d2 = 0, e2 = 0, f2 = 0] = m2;
    return [
        a1 * a2 + c1 * b2,
        b1 * a2 + d1 * b2,
        a1 * c2 + c1 * d2,
        b1 * c2 + d1 * d2,
        a1 * e2 + c1 * f2 + e1,
        b1 * e2 + d1 * f2 + f1,
    ];
};

// Where an item sits on the page, given the page's viewport at scale 1
// (`transform`, `width`, `height`).
export const itemRect = (
    item: PdfTextItem,
    viewport: {
        readonly transform: readonly number[];
        readonly width: number;
        readonly height: number;
    },
): PageRect => {
    const [, , c = 0, d = 0, e = 0, f = 0] = multiply(
        viewport.transform,
        item.transform,
    );
    const fontHeight = Math.hypot(c, d) || item.height;
    return {
        left: e / viewport.width,
        top: (f - fontHeight) / viewport.height,
        width: item.width / viewport.width,
        height: fontHeight / viewport.height,
    };
};
